// Что вернул сервер после постановки или перевода: предупреждения и изменение
// укомплектованности объекта.
//
// Предупреждение — не ошибка. «Человек занят на другом объекте» может означать и накладку, и
// нормальную работу на двух постах; решает человек, а система обязана сказать. Поэтому это
// жёлтая плашка со списком, а не красный отказ и не молчание.
//
// Укомплектованность пишется словами и с дельтой: «было 4, стало 5 из 6 — не хватает одного».
// Голое «5/6» человек читает как оценку, а не как недобор.

import { RiAlertLine, RiGroupLine } from '@remixicon/react';

import type { AssignmentWarning, StaffingDelta } from '@/shared/api/endpoints/people';
import { countLabel } from '@/shared/text/plural';
import * as Alert from '@/shared/ui/alert';

import { PERSON_FORMS } from '../dictionaries';

export function WarningsNotice({
  warnings,
  сделано = 'Назначение уже создано',
}: {
  warnings: readonly AssignmentWarning[];
  /**
   * Что уже случилось, первой половиной последней строки. У постановки — «создано», у
   * продления — «продлено»: замечание приходит ВМЕСТЕ с выполненным действием, и сказать
   * «создано» о продлённом назначении значило бы соврать о том, что произошло.
   */
  сделано?: string;
}) {
  if (warnings.length === 0) {
    return null;
  }
  return (
    <Alert.Root variant='light' status='warning' size='small' role='status'>
      <Alert.Icon as={RiAlertLine} />
      <div className='flex min-w-0 flex-col gap-1'>
        <span>
          {warnings.length === 1
            ? 'Одно замечание по этому назначению'
            : `Замечаний по этому назначению: ${warnings.length}`}
        </span>
        <ul className='flex flex-col gap-0.5'>
          {warnings.map((warning) => (
            <li key={warning.code} className='text-paragraph-xs'>
              {warning.message}
              {warning.hint !== null && warning.hint !== undefined && (
                <span className='opacity-80'> — {warning.hint}</span>
              )}
            </li>
          ))}
        </ul>
        <span className='text-paragraph-xs opacity-80'>
          {сделано}: это замечания, а не отказ. Решать по каждому вам.
        </span>
      </div>
    </Alert.Root>
  );
}

export function StaffingNotice({
  staffing,
  siteName,
}: {
  staffing: StaffingDelta | null;
  siteName: string;
}) {
  if (staffing === null) {
    return null;
  }
  return (
    <div className='flex items-start gap-3 rounded-xl bg-bg-white-0 p-4 ring-1 ring-inset ring-stroke-soft-200'>
      <div className='flex size-10 shrink-0 items-center justify-center rounded-full bg-bg-weak-50'>
        <RiGroupLine className='size-5 text-text-sub-600' />
      </div>
      <p className='text-paragraph-sm text-text-strong-950'>{staffingWords(staffing, siteName)}</p>
    </div>
  );
}

/** Укомплектованность словами: было, стало, требуется и что с этим делать. */
export function staffingWords(staffing: StaffingDelta, siteName: string): string {
  const head = `Объект «${siteName}»: было назначено ${staffing.assigned_before}, стало ${countLabel(
    staffing.assigned_after,
    PERSON_FORMS,
  )} при требуемых ${staffing.required}.`;
  const delta = staffing.assigned_after - staffing.required;
  if (delta === 0) {
    return `${head} Объект укомплектован ровно.`;
  }
  if (delta < 0) {
    return `${head} Не хватает ещё ${countLabel(
      -delta,
      PERSON_FORMS,
    )}: пока недобор не закрыт, посты придётся закрывать переработками уже назначенных.`;
  }
  return `${head} Назначено на ${countLabel(
    delta,
    PERSON_FORMS,
  )} больше, чем требуется постами: проверьте, не остались ли живыми назначения тех, кто уже ушёл.`;
}
